import { useState, useEffect, useCallback } from 'react';
import {
  getAnalyticsSummary,
  getUserAnalytics,
  getActivityAnalytics,
  getLearningAnalytics,
  getRevenueAnalytics,
  getGeoAnalytics,
} from '../services/adminApiClient';
import type {
  AnalyticsSummary,
  UserAnalytics,
  ActivityAnalytics,
  LearningAnalytics,
  RevenueAnalytics,
  GeoAnalytics,
} from '../types';

const TABS = ['Overview', 'User Growth', 'Activity', 'Learning', 'Geographic Distribution', 'Revenue'] as const;
type Tab = typeof TABS[number];

type Range = '7d' | '30d' | '90d';

// ─── Shared primitives ────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-7 w-7 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin" />
    </div>
  );
}

function KpiCard({ label, value, sub, color = 'text-slate-900' }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function RangeSelector({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500">Range:</span>
      {(['7d', '30d', '90d'] as Range[]).map(r => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${value === r ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

/** Horizontal bar chart */
function HBar({ label, value, max, colorClass = 'bg-indigo-400', suffix = '' }: {
  label: string; value: number; max: number; colorClass?: string; suffix?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-40 shrink-0 truncate text-xs text-slate-600" title={label}>{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
        <div className={`h-2 rounded-full ${colorClass}`} style={{ width: max ? `${(value / max) * 100}%` : '0%' }} />
      </div>
      <span className="w-14 text-right text-xs font-medium text-slate-700">{value.toLocaleString()}{suffix}</span>
    </div>
  );
}

function _niceMax(raw: number): number {
  if (raw <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  const rounded = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return rounded * mag;
}

function _fmtY(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return String(v);
}

const Y_FRACS = [0, 0.25, 0.5, 0.75, 1];

/** SVG line chart with area fill, Y-axis ticks, gridlines, X-axis dates */
function LineChart({ data, color = '#8b5cf6' }: { data: { date: string; count: number }[]; color?: string }) {
  if (data.length === 0) return <p className="text-sm text-slate-400 italic">No data</p>;

  const niceMax = _niceMax(Math.max(...data.map(d => d.count), 1));
  const SVG_W = 800;
  const CHART_X = 44;
  const CHART_Y = 10;
  const CHART_H = 140;
  const CHART_W = SVG_W - CHART_X - 8;
  const SVG_H = CHART_Y + CHART_H + 28;
  const n = data.length;

  const toX = (i: number) => CHART_X + (n <= 1 ? CHART_W / 2 : (i / (n - 1)) * CHART_W);
  const toY = (v: number) => CHART_Y + CHART_H - (v / niceMax) * CHART_H;

  const linePoints = data.map((d, i) => `${toX(i)},${toY(d.count)}`).join(' ');
  const areaPoints = [
    `${toX(0)},${CHART_Y + CHART_H}`,
    ...data.map((d, i) => `${toX(i)},${toY(d.count)}`),
    `${toX(n - 1)},${CHART_Y + CHART_H}`,
  ].join(' ');

  const showEveryN = Math.max(1, Math.ceil(n / 10));

  return (
    <svg width="100%" viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ overflow: 'visible' }}>
      {/* Gridlines + Y-axis labels */}
      {Y_FRACS.map(f => {
        const y = CHART_Y + CHART_H - f * CHART_H;
        return (
          <g key={f}>
            <line x1={CHART_X} y1={y} x2={SVG_W - 8} y2={y}
              stroke={f === 0 ? '#cbd5e1' : '#f1f5f9'} strokeWidth="1" />
            <text x={CHART_X - 4} y={y} textAnchor="end" dominantBaseline="middle"
              fontSize="11" fill="#94a3b8">
              {_fmtY(Math.round(f * niceMax))}
            </text>
          </g>
        );
      })}
      {/* Y-axis border */}
      <line x1={CHART_X} y1={CHART_Y} x2={CHART_X} y2={CHART_Y + CHART_H} stroke="#e2e8f0" strokeWidth="1" />
      {/* Area fill */}
      <polygon points={areaPoints} fill={color} fillOpacity="0.12" />
      {/* Line */}
      <polyline points={linePoints} fill="none" stroke={color} strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />
      {/* Dots */}
      {data.map((d, i) => (
        <circle key={d.date} cx={toX(i)} cy={toY(d.count)} r="3.5"
          fill={color} stroke="white" strokeWidth="1.5">
          <title>{d.date}: {d.count.toLocaleString()}</title>
        </circle>
      ))}
      {/* X-axis labels */}
      {data.map((d, i) => (
        (i % showEveryN === 0 || i === n - 1) && (
          <text key={d.date} x={toX(i)} y={CHART_Y + CHART_H + 16}
            textAnchor="middle" fontSize="10" fill="#94a3b8">
            {d.date.slice(5)}
          </text>
        )
      ))}
    </svg>
  );
}

/** Vertical bar chart with Y-axis scale and properly aligned X-axis date labels */
function VBarChart({ data, keys, colors, height = 160 }: {
  data: Record<string, string | number>[];
  keys: string[];
  colors: string[];
  height?: number;
}) {
  const rawMax = Math.max(...data.flatMap(d => keys.map(k => Number(d[k]) || 0)), 1);
  const maxVal = _niceMax(rawMax);
  // Show ~10 evenly-spaced labels regardless of range
  const showEveryN = Math.max(1, Math.ceil(data.length / 10));

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-1">
        {/* Y-axis label column — absolutely positioned to align with gridlines */}
        <div className="relative shrink-0 w-8" style={{ height }}>
          {Y_FRACS.map(f => (
            <span
              key={f}
              className="absolute right-1 text-[10px] leading-none text-slate-400 -translate-y-1/2"
              style={{ bottom: `${f * 100}%` }}
            >
              {_fmtY(Math.round(f * maxVal))}
            </span>
          ))}
        </div>

        {/* Chart body */}
        <div className="flex-1 min-w-0">
          {/* Bar area with L-shaped axis border and gridlines */}
          <div className="relative border-l border-b border-slate-200" style={{ height }}>
            {/* Horizontal gridlines at 25 / 50 / 75 / 100% */}
            {Y_FRACS.filter(f => f > 0).map(f => (
              <div
                key={f}
                className="absolute left-0 right-0 border-t border-slate-100"
                style={{ bottom: `${f * 100}%` }}
              />
            ))}

            {/* Bar columns — flex items-end keeps all bars pinned to the baseline */}
            <div className="absolute inset-0 flex items-end gap-0.5">
              {data.map((d) => (
                <div key={String(d.date)} className="flex-1 flex items-end gap-px min-w-[8px] h-full">
                  {keys.map((k, ki) => {
                    const v = Number(d[k]) || 0;
                    const pct = Math.min((v / maxVal) * 100, 100);
                    return (
                      <div
                        key={k}
                        className={`flex-1 rounded-t-sm ${colors[ki]} transition-all`}
                        style={{ height: `${pct}%` }}
                        title={`${k}: ${v}`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* X-axis label row — separate from bar layout so it never affects bar heights */}
          <div className="flex gap-0.5 pt-1">
            {data.map((d, i) => (
              <div key={String(d.date)} className="flex-1 min-w-[8px] flex justify-center overflow-visible">
                {i % showEveryN === 0 && (
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">
                    {String(d.date).slice(5)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 1: Overview ──────────────────────────────────────────────────────────

function OverviewTab() {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAnalyticsSummary().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Users</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiCard label="Total Users" value={data.total_users} />
          <KpiCard label="Active (7d)" value={data.active_7d} sub="had a tutor session" />
          <KpiCard label="Active (30d)" value={data.active_30d} sub="had a tutor session" />
          <KpiCard label="Paying Users" value={data.paying_users} color={data.paying_users > 0 ? 'text-emerald-600' : 'text-slate-900'} />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">New Signups</p>
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label="Today" value={data.new_signups_today} />
          <KpiCard label="This Week" value={data.new_signups_week} />
          <KpiCard label="This Month" value={data.new_signups_month} />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Sessions</p>
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label="Today" value={data.sessions_today} />
          <KpiCard label="This Week" value={data.sessions_this_week} />
          <KpiCard label="This Month" value={data.sessions_this_month} />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Attention</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <KpiCard
            label="Pending Content Flags"
            value={data.pending_flags}
            color={data.pending_flags > 0 ? 'text-amber-600' : 'text-slate-900'}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Tab 2: User Growth ───────────────────────────────────────────────────────

function UserGrowthTab() {
  const [range, setRange] = useState<Range>('30d');
  const [data, setData] = useState<UserAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((r: Range) => {
    setLoading(true);
    getUserAnalytics(r).then(res => setData(res.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  const handleRange = (r: Range) => { setRange(r); };

  return (
    <div className="space-y-6">
      <RangeSelector value={range} onChange={handleRange} />

      {loading ? <Spinner /> : data && (
        <>
          {/* Daily signups chart */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Daily New Signups</h3>
            <VBarChart
              data={data.daily_signups.map(d => ({ date: d.date, signups: d.count }))}
              keys={['signups']}
              colors={['bg-indigo-400']}
            />
          </div>

          {/* Cumulative growth */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Cumulative Users</h3>
            <LineChart data={data.cumulative} color="#8b5cf6" />
          </div>

          {/* User Journey Funnel */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-sm font-semibold text-slate-700">User Journey Funnel</h3>
                <p className="text-xs text-slate-400 mt-0.5">All-time · each bar is % of registered users</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-slate-400">Registered → Paid</p>
                <p className={`text-lg font-bold ${data.onboarding_funnel.upgraded_to_paid > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {data.onboarding_funnel.registered > 0
                    ? ((data.onboarding_funnel.upgraded_to_paid / data.onboarding_funnel.registered) * 100).toFixed(1)
                    : '0.0'}%
                </p>
              </div>
            </div>

            {(() => {
              type FKey = keyof typeof data.onboarding_funnel;
              const steps: { key: FKey; label: string; color: string; desc: string }[] = [
                { key: 'registered',           label: 'Registered',              color: 'bg-blue-400',    desc: 'Created an account' },
                { key: 'onboarding_completed', label: 'Completed Onboarding',    color: 'bg-indigo-400',  desc: 'Filled profile & goals' },
                { key: 'had_tutor_session',    label: 'Had First Tutor Session', color: 'bg-violet-400',  desc: 'Core feature activated' },
                { key: 'had_exam_session',     label: 'Had First Exam',          color: 'bg-purple-400',  desc: 'Exam feature used' },
                { key: 'had_practice_session', label: 'Had First Practice',      color: 'bg-fuchsia-400', desc: 'Practice feature used' },
                { key: 'upgraded_to_paid',     label: 'Upgraded to Paid',        color: 'bg-emerald-500', desc: 'Revenue conversion' },
              ];
              const total = data.onboarding_funnel.registered || 1;

              return (
                <div>
                  {steps.map((step, i) => {
                    const value = data.onboarding_funnel[step.key];
                    const pct = Math.round((value / total) * 100);
                    const prev = i > 0 ? data.onboarding_funnel[steps[i - 1].key] : null;
                    const dropped = prev !== null ? prev - value : null;
                    const convPct = prev && prev > 0 ? Math.round((value / prev) * 100) : null;
                    const anomaly = prev !== null && value > prev;

                    return (
                      <div key={step.key}>
                        {/* Connector between steps */}
                        {i > 0 && (
                          <div className="flex items-center gap-2 py-1.5 pl-6">
                            <span className="text-slate-200 text-xs leading-none">│</span>
                            {anomaly ? (
                              <span className="text-xs text-slate-400 italic">↓ some users bypassed this step</span>
                            ) : dropped === 0 ? (
                              <span className="text-xs text-emerald-500">↓ 100% continued</span>
                            ) : (
                              <span className={`text-xs ${convPct! >= 50 ? 'text-emerald-500' : convPct! >= 25 ? 'text-amber-500' : 'text-red-400'}`}>
                                ↓ {convPct}% continued
                                <span className="text-slate-400 ml-1">· {dropped!.toLocaleString()} dropped off</span>
                              </span>
                            )}
                          </div>
                        )}

                        {/* Step row */}
                        <div className="flex items-center gap-3 py-1.5">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0 ${step.color}`}>
                            {i + 1}
                          </div>
                          <span className="w-56 shrink-0 text-sm text-slate-700" title={step.desc}>{step.label}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                            <div className={`h-3 rounded-full ${step.color} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-12 text-right text-sm font-semibold text-slate-800">{value.toLocaleString()}</span>
                          <span className="w-10 text-right text-xs text-slate-400">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Exam target distribution */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Exam Targets</h3>
              {data.exam_target_distribution.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {data.exam_target_distribution.map(e => (
                    <HBar key={e.exam_target} label={e.exam_target} value={e.count}
                      max={data.exam_target_distribution[0]?.count || 1} colorClass="bg-sky-400" />
                  ))}
                </div>
              )}
            </div>

            {/* Skill level distribution */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Skill Levels</h3>
              {data.skill_level_distribution.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {data.skill_level_distribution.map(s => (
                    <HBar key={s.skill_level} label={s.skill_level} value={s.count}
                      max={data.skill_level_distribution[0]?.count || 1} colorClass="bg-emerald-400" />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab 3: Activity & Engagement ─────────────────────────────────────────────

function ActivityTab() {
  const [range, setRange] = useState<Range>('30d');
  const [data, setData] = useState<ActivityAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((r: Range) => {
    setLoading(true);
    getActivityAnalytics(r).then(res => setData(res.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  return (
    <div className="space-y-6">
      <RangeSelector value={range} onChange={(r) => setRange(r)} />

      {loading ? <Spinner /> : data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label="Tutor Sessions" value={data.feature_totals.tutor} sub="all time" />
            <KpiCard label="Exam Sessions" value={data.feature_totals.exam} sub="all time" />
            <KpiCard label="Practice Sessions" value={data.feature_totals.practice} sub="all time" />
            <KpiCard label="Smart Notes" value={data.feature_totals.notes} sub="all time" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <KpiCard label="Avg Sessions / Active User" value={data.avg_sessions_per_active_user} sub={`in last ${range}`} />
          </div>

          {/* Daily activity stacked chart */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-1">Daily Sessions by Type</h3>
            <div className="flex gap-4 mb-3">
              {[
                { label: 'Tutor', color: 'bg-indigo-400' },
                { label: 'Exam', color: 'bg-amber-400' },
                { label: 'Practice', color: 'bg-emerald-400' },
              ].map(l => (
                <span key={l.label} className="flex items-center gap-1 text-xs text-slate-500">
                  <span className={`inline-block h-2.5 w-2.5 rounded-sm ${l.color}`} />
                  {l.label}
                </span>
              ))}
            </div>
            <VBarChart
              data={data.daily_activity.map(d => ({ date: d.date, tutor: d.tutor, exam: d.exam, practice: d.practice }))}
              keys={['tutor', 'exam', 'practice']}
              colors={['bg-indigo-400', 'bg-amber-400', 'bg-emerald-400']}
              height={140}
            />
          </div>

          {/* Feature split bar */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Feature Usage Split (All Time)</h3>
            {(() => {
              const ft = data.feature_totals;
              const total = ft.tutor + ft.exam + ft.practice + ft.notes;
              const items = [
                { label: 'Tutor Sessions', value: ft.tutor, color: 'bg-indigo-400' },
                { label: 'Exam Sessions', value: ft.exam, color: 'bg-amber-400' },
                { label: 'Practice Sessions', value: ft.practice, color: 'bg-emerald-400' },
                { label: 'Smart Notes', value: ft.notes, color: 'bg-sky-400' },
              ];
              return total === 0 ? (
                <p className="text-sm text-slate-400 italic">No activity recorded yet</p>
              ) : (
                <div className="space-y-2">
                  {items.map(item => (
                    <HBar key={item.label} label={item.label} value={item.value}
                      max={total} colorClass={item.color}
                      suffix={` (${total > 0 ? Math.round((item.value / total) * 100) : 0}%)`} />
                  ))}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab 4: Learning Analytics ────────────────────────────────────────────────

function LearningTab() {
  const [data, setData] = useState<LearningAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLearningAnalytics().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return null;

  const maxLearners = data.top_topics[0]?.learner_count || 1;

  return (
    <div className="space-y-6">
      {/* Top topics */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Top Topics by Learner Count</h3>
        </div>
        {data.top_topics.length === 0 ? (
          <p className="p-4 text-sm text-slate-400 italic">No topic data yet</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">Topic</th>
                <th className="px-4 py-2.5 text-left">Learners</th>
                <th className="px-4 py-2.5 text-left w-48">Avg Mastery</th>
                <th className="px-4 py-2.5 text-right">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.top_topics.map(t => (
                <tr key={t.topic} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-800 font-medium max-w-xs truncate" title={t.topic}>{t.topic}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        <div className="h-1.5 rounded-full bg-indigo-400" style={{ width: `${(t.learner_count / maxLearners) * 100}%` }} />
                      </div>
                      <span className="text-slate-600">{t.learner_count}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        {/* mastery is a 0–1 fraction, as on the Content page */}
                        <div
                          className={`h-1.5 rounded-full ${t.avg_mastery >= 0.5 ? 'bg-emerald-400' : t.avg_mastery >= 0.2 ? 'bg-amber-400' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(t.avg_mastery * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{(t.avg_mastery * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

// ─── Tab 5: Geographic Distribution ──────────────────────────────────────────

const TREND_COLORS = ['bg-violet-500', 'bg-sky-400', 'bg-amber-400', 'bg-slate-300'];
const TREND_LABEL_COLORS = ['text-violet-600', 'text-sky-600', 'text-amber-600', 'text-slate-500'];
const TREND_LINE_COLORS = ['#8b5cf6', '#38bdf8', '#fbbf24', '#cbd5e1'];
const TOTAL_LINE_COLOR = '#10b981'; // emerald-500 — reserved for Total

const TOTAL_KEY = '__total__';

function GeoLineChart({
  monthly_trend,
  trend_countries,
}: {
  monthly_trend: Record<string, string | number>[];
  trend_countries: string[];
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setHidden(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  if (monthly_trend.length === 0) return <p className="text-sm text-slate-400 italic">No monthly data yet</p>;

  // Per-month totals (sum of all countries, regardless of hidden state)
  const totals = monthly_trend.map(mo =>
    trend_countries.reduce((s, c) => s + (Number(mo[c]) || 0), 0)
  );

  // Scale from visible lines only
  const scaleVals: number[] = [];
  if (!hidden.has(TOTAL_KEY)) scaleVals.push(...totals);
  trend_countries.forEach(c => {
    if (!hidden.has(c)) monthly_trend.forEach(mo => scaleVals.push(Number(mo[c]) || 0));
  });
  const niceMax = _niceMax(Math.max(...scaleVals, 1));

  const SVG_W = 800;
  const CHART_X = 44;
  const CHART_Y = 10;
  const CHART_H = 150;
  const CHART_W = SVG_W - CHART_X - 8;
  const SVG_H = CHART_Y + CHART_H + 28;
  const n = monthly_trend.length;

  const toX = (i: number) => CHART_X + (n <= 1 ? CHART_W / 2 : (i / (n - 1)) * CHART_W);
  const toY = (v: number) => CHART_Y + CHART_H - (v / niceMax) * CHART_H;

  const allHidden = hidden.has(TOTAL_KEY) && trend_countries.every(c => hidden.has(c));

  return (
    <div>
      {/* Clickable legend */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {/* Country pills */}
        {trend_countries.map((c, i) => {
          const isHidden = hidden.has(c);
          const color = TREND_LINE_COLORS[i] ?? '#cbd5e1';
          return (
            <button
              key={c}
              onClick={() => toggle(c)}
              title={isHidden ? `Show ${c}` : `Hide ${c}`}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all select-none cursor-pointer ${
                isHidden
                  ? 'border-slate-200 bg-slate-50 text-slate-400 opacity-50'
                  : `border-slate-200 bg-white ${TREND_LABEL_COLORS[i] ?? 'text-slate-600'} shadow-sm`
              }`}
            >
              <svg width="16" height="8" viewBox="0 0 16 8">
                <line x1="0" y1="4" x2="16" y2="4"
                  stroke={isHidden ? '#cbd5e1' : color} strokeWidth="2" strokeLinecap="round" />
                <circle cx="8" cy="4" r="2.5" fill={isHidden ? '#cbd5e1' : color} />
              </svg>
              {c}
            </button>
          );
        })}

        {/* Total pill */}
        {(() => {
          const isHidden = hidden.has(TOTAL_KEY);
          return (
            <button
              onClick={() => toggle(TOTAL_KEY)}
              title={isHidden ? 'Show Total' : 'Hide Total'}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-semibold transition-all select-none cursor-pointer ${
                isHidden
                  ? 'border-slate-200 bg-slate-50 text-slate-400 opacity-50'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm'
              }`}
            >
              <svg width="16" height="8" viewBox="0 0 16 8">
                <line x1="0" y1="4" x2="16" y2="4"
                  stroke={isHidden ? '#cbd5e1' : TOTAL_LINE_COLOR}
                  strokeWidth="2.5" strokeLinecap="round" strokeDasharray="5 2.5" />
                <circle cx="8" cy="4" r="2.5" fill={isHidden ? '#cbd5e1' : TOTAL_LINE_COLOR} />
              </svg>
              Total
            </button>
          );
        })()}
      </div>

      {/* Chart or empty state */}
      {allHidden ? (
        <p className="text-sm text-slate-400 italic text-center py-10">
          No lines selected — click a country or Total above to show its trend
        </p>
      ) : (
        <svg width="100%" viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ overflow: 'visible' }}>
          {/* Gridlines + Y-axis labels */}
          {Y_FRACS.map(f => {
            const y = CHART_Y + CHART_H - f * CHART_H;
            return (
              <g key={f}>
                <line x1={CHART_X} y1={y} x2={SVG_W - 8} y2={y}
                  stroke={f === 0 ? '#cbd5e1' : '#f1f5f9'} strokeWidth="1" />
                <text x={CHART_X - 4} y={y} textAnchor="end" dominantBaseline="middle"
                  fontSize="11" fill="#94a3b8">
                  {_fmtY(Math.round(f * niceMax))}
                </text>
              </g>
            );
          })}
          {/* Y-axis border */}
          <line x1={CHART_X} y1={CHART_Y} x2={CHART_X} y2={CHART_Y + CHART_H}
            stroke="#e2e8f0" strokeWidth="1" />

          {/* Country lines (rendered before Total so Total sits on top) */}
          {trend_countries.map((country, ci) => {
            if (hidden.has(country)) return null;
            const color = TREND_LINE_COLORS[ci] ?? '#cbd5e1';
            const pts = monthly_trend
              .map((mo, i) => `${toX(i)},${toY(Number(mo[country]) || 0)}`)
              .join(' ');
            return (
              <g key={country}>
                <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
                  strokeLinejoin="round" strokeLinecap="round" />
                {monthly_trend.map((mo, i) => (
                  <circle key={i} cx={toX(i)} cy={toY(Number(mo[country]) || 0)} r="3.5"
                    fill={color} stroke="white" strokeWidth="1.5">
                    <title>{country} · {String(mo.label)}: {Number(mo[country]) || 0}</title>
                  </circle>
                ))}
              </g>
            );
          })}

          {/* Total line — rendered last so it renders on top of country lines */}
          {!hidden.has(TOTAL_KEY) && (
            <g>
              <polyline
                points={totals.map((v, i) => `${toX(i)},${toY(v)}`).join(' ')}
                fill="none" stroke={TOTAL_LINE_COLOR} strokeWidth="2.5"
                strokeLinejoin="round" strokeLinecap="round" strokeDasharray="6 3"
              />
              {totals.map((v, i) => (
                <circle key={i} cx={toX(i)} cy={toY(v)} r="4"
                  fill={TOTAL_LINE_COLOR} stroke="white" strokeWidth="1.5">
                  <title>Total · {String(monthly_trend[i].label)}: {v}</title>
                </circle>
              ))}
            </g>
          )}

          {/* X-axis month labels */}
          {monthly_trend.map((mo, i) => (
            <text key={i} x={toX(i)} y={CHART_Y + CHART_H + 16}
              textAnchor="middle" fontSize="10" fill="#94a3b8">
              {String(mo.label)}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}

function GeographicTab() {
  const [data, setData] = useState<GeoAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGeoAnalytics().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return null;

  const { all_time, total_all_time, country_growth, monthly_trend, trend_countries } = data;


  return (
    <div className="space-y-6">

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Countries" value={all_time.length} />
        <KpiCard label="Users with Country Data" value={total_all_time} />
        <KpiCard label="Active This Month" value={country_growth.reduce((s, r) => s + r.this_month, 0)} sub="new signups" />
        <KpiCard label="Active Users (30d)" value={country_growth.reduce((s, r) => s + r.active_30d, 0)} sub="had a session" />
      </div>

      {all_time.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-slate-400 italic">No country data recorded yet</p>
        </div>
      ) : (
        <>
          {/* ── Section 1: Country Growth Table ─────────────────────────────── */}
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Country Growth</h3>
              <span className="text-xs text-slate-400">Signups this month vs last · Active = had a session in 30d</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left">Country</th>
                  <th className="px-4 py-2.5 text-right">This Month</th>
                  <th className="px-4 py-2.5 text-right">vs Last Month</th>
                  <th className="px-4 py-2.5 text-right">Active (30d)</th>
                  <th className="px-4 py-2.5 text-right">Activation Rate</th>
                  <th className="px-4 py-2.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {country_growth.map(row => {
                  const momPositive = row.mom_change > 0;
                  const momNeutral  = row.mom_change === 0;
                  return (
                    <tr key={row.country} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-800">{row.country}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700">{row.this_month}</td>
                      <td className="px-4 py-2.5 text-right">
                        {momNeutral ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className={momPositive ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>
                            {momPositive ? '↑' : '↓'} {Math.abs(row.mom_change)}
                            {row.mom_pct !== null && (
                              <span className="ml-1 text-xs opacity-70">({Math.abs(row.mom_pct)}%)</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-700">{row.active_30d}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          row.activation_rate >= 50 ? 'bg-emerald-100 text-emerald-700'
                          : row.activation_rate >= 20 ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-500'
                        }`}>
                          {row.activation_rate}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{row.total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Section 2: Monthly Trend Chart ──────────────────────────────── */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-1">Monthly Signup Trend by Country</h3>
            <p className="text-xs text-slate-400 mb-4">Click a country to show / hide its line</p>
            <GeoLineChart monthly_trend={monthly_trend} trend_countries={trend_countries} />
          </div>

          {/* ── Section 3: All-time Distribution ────────────────────────────── */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">All-time Distribution</h3>
            <div className="space-y-3">
              {all_time.map(c => {
                const pct = total_all_time > 0 ? Math.round((c.count / total_all_time) * 100) : 0;
                return (
                  <div key={c.country} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 text-sm text-slate-700 font-medium truncate" title={c.country}>
                      {c.country}
                    </span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-2.5 rounded-full bg-violet-400"
                        style={{ width: `${(c.count / (all_time[0]?.count || 1)) * 100}%` }}
                      />
                    </div>
                    <span className="w-20 text-right text-sm text-slate-600">
                      {c.count} <span className="text-slate-400 text-xs">({pct}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab 6: Revenue ───────────────────────────────────────────────────────────

function RevenueTab() {
  const [data, setData] = useState<RevenueAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRevenueAnalytics().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return null;

  const PLAN_COLORS: Record<string, string> = {
    free: 'bg-slate-300', student: 'bg-sky-400', pro: 'bg-indigo-400', premium: 'bg-violet-500',
  };
  const STATUS_COLORS: Record<string, string> = {
    active: 'bg-emerald-400', cancelled: 'bg-red-400', expired: 'bg-slate-300',
  };

  const totalSubs = data.plan_distribution.reduce((s, p) => s + p.count, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Plan distribution */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Plan Distribution</h3>
          <div className="space-y-2">
            {data.plan_distribution.map(p => (
              <div key={p.plan_id}>
                <HBar
                  label={`${p.plan_id.charAt(0).toUpperCase()}${p.plan_id.slice(1)}`}
                  value={p.count}
                  max={totalSubs || 1}
                  colorClass={PLAN_COLORS[p.plan_id] ?? 'bg-slate-300'}
                  suffix={` (${totalSubs > 0 ? Math.round((p.count / totalSubs) * 100) : 0}%)`}
                />
              </div>
            ))}
          </div>
          {data.plan_distribution.length === 0 && (
            <p className="text-sm text-slate-400 italic">No subscriptions yet</p>
          )}
        </div>

        {/* Subscription status */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Subscription Status</h3>
          <div className="space-y-2">
            {data.subscription_status.map(s => (
              <HBar key={s.status} label={s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                value={s.count} max={totalSubs || 1}
                colorClass={STATUS_COLORS[s.status] ?? 'bg-slate-300'} />
            ))}
          </div>
          {data.subscription_status.length === 0 && (
            <p className="text-sm text-slate-400 italic">No subscription data yet</p>
          )}
        </div>
      </div>

      {/* Revenue by currency */}
      {data.revenue_by_currency.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Revenue by Currency (Successful Payments)</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {data.revenue_by_currency.map(r => (
              <div key={r.currency} className="rounded-lg bg-slate-50 p-3 text-center">
                <p className="text-xs text-slate-500 mb-1">{r.currency}</p>
                <p className="text-lg font-bold text-slate-800">
                  {r.currency === 'INR' ? '₹' : r.currency === 'USD' ? '$' : r.currency === 'GBP' ? '£' : ''}
                  {(r.total / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment timeline */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Payment Events (Last 30 Days)</h3>
        {data.payment_timeline.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No payment events in the last 30 days</p>
        ) : (
          <VBarChart
            data={data.payment_timeline.map(d => ({ date: d.date, events: d.count }))}
            keys={['events']}
            colors={['bg-emerald-400']}
          />
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Analytics() {
  const [tab, setTab] = useState<Tab>('Overview');

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">Analytics & Reporting</h1>

      <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
              tab === t
                ? 'border-b-2 border-indigo-600 text-indigo-600 bg-white -mb-px'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview'                && <OverviewTab />}
      {tab === 'User Growth'             && <UserGrowthTab />}
      {tab === 'Activity'                && <ActivityTab />}
      {tab === 'Learning'                && <LearningTab />}
      {tab === 'Geographic Distribution' && <GeographicTab />}
      {tab === 'Revenue'                 && <RevenueTab />}
    </div>
  );
}
